import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { createPortal } from "react-dom";

import type { MapContextType } from "@/context/MapProvider";
import { useMap } from "@/hooks/map/core/useMapContext";

import type { SurfaceModel } from "@a2ui/web_core/v0_9";
import dayjs from "dayjs";

import { ReportPrintHeader } from "./ReportPrintHeader";

import { ReportSurface } from "./a2ui/ReportSurface";

interface PrintCopy {
  surface: SurfaceModel;
  mapImage: string | null;
}

function captureMap(map: MapContextType["map"]): string | null {
  try {
    return map?.getCanvas().toDataURL("image/png") ?? null;
  } catch {
    return null;
  }
}

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

// The print copy mounts only when the user exports, and print reads its content
// as soon as this resolves. The charts (ResponsiveContainer) need one frame to
// measure the copy and one more to draw at that size. Without this wait, the
// PDF gets charts that are empty or not drawn yet.
async function waitForCharts() {
  await nextFrame();
  await nextFrame();
}

/**
 * Prints a report surface as a PDF (the user picks "Save as PDF").
 *
 * It prints a second render of the surface at page width, not the panel, so
 * the PDF looks the same in a narrow, maximized or wide panel. Render
 * `printCopyPortal` once, anywhere in the tree, for `exportPdf` to work.
 */
export function useReportPdfExport() {
  const { map } = useMap();
  const printRootRef = useRef<HTMLDivElement>(null);

  const [printCopy, setPrintCopy] = useState<PrintCopy | null>(null);

  const closePrintCopy = () => setPrintCopy(null);

  const print = useReactToPrint({
    contentRef: printRootRef,
    documentTitle: `AnalysisReport_${dayjs().format("YYYYMMDDHHmm")}`,
    bodyClass: "report-print-body",
    onBeforePrint: waitForCharts,
    onAfterPrint: closePrintCopy,
    onPrintError: closePrintCopy
  });

  const exportPdf = (surface: SurfaceModel) => {
    if (printCopy) return;

    setPrintCopy({ surface, mapImage: captureMap(map) });
    print();
  };

  const printCopyPortal =
    printCopy &&
    createPortal(
      <div aria-hidden className="fixed -left-[10000px] top-0">
        <div
          ref={printRootRef}
          className="report-print-root w-[180mm] bg-white [&_.legends]:max-h-none [&_.legends]:overflow-visible"
        >
          <ReportPrintHeader />
          {printCopy.mapImage && (
            <img
              src={printCopy.mapImage}
              alt="Map snapshot"
              className="mb-4 max-h-[500px] w-full object-cover"
            />
          )}
          <ReportSurface surface={printCopy.surface} isPrint />
        </div>
      </div>,
      document.body
    );

  return { exportPdf, printCopyPortal };
}
